/* eslint-disable @typescript-eslint/naming-convention */
import {Injectable} from '@angular/core';
import {UserLoginResult} from '~/app/core/model/user-login-result';
import {LocationService} from '~/app/core/location.service';
import {UserInfo} from '~/app/core/model/user-info';
import {OfferInfo} from '~/app/core/model/offer-info';
import {OfferSearch} from '~/app/core/model/offer-search';
import {Settings} from '~/app/core/model/settings';
import {SettingsService} from '~/app/core/settings.service';
import {UserProfileService} from '~/app/core/user-profile-service/user-profile.service';
import {SuccessResult} from '~/app/core/model/connect';
import {InitInfo} from '~/app/core/model/init-info';
import {OfferService} from '~/app/core/offer-service/offer.service';
import {InteractionService, InteractionTimer} from '~/app/core/interaction.service';
import {InteractionType} from '~/app/core/model/interaction-type';
import {InteractionRefType} from '~/app/core/model/interaction-ref-type';
import {InteractionComponent} from '~/app/core/model/interaction-component';
import {AlternateLocationsList} from '~/app/core/model/rpc/alternate-locations-list';
import {LocationInfoRPC} from '~/app/core/model/rpc/location-info-rpc';
import {LocationInfo} from '~/app/core/model/location-info';
import {SponsorInfo} from '~/app/core/model/sponsor-info';
import {InteractionOffer} from '~/app/core/model/interaction/interaction-offer';
import {InteractionAd} from '~/app/core/model/interaction/interaction-ad';
import {InteractionOther} from '~/app/core/model/interaction/interaction-other';
import {UserProperty} from '~/app/core/model/user-property';
import {StorageService} from '~/app/core/storage.service';
import {OfferAvailableInfo} from '~/app/core/model/rpc/offer-available-info';
import {NotificationService} from '~/app/core/notification.service';
import {OfferSearchResultLocation} from '~/app/core/model/offer-search-result-location';
import {OfferFilter} from '~/app/core/model/offer-filter';
import {OfferFilterOptions} from '~/app/core/model/offer-filter-options';
import {TicketService} from '~/app/core/model/ticket-service/ticket.service';

@Injectable()
export class ApiService {
    constructor(
        private readonly storageService: StorageService,
        private readonly userProfileService: UserProfileService,
        private readonly ticketService: TicketService,
        private readonly locationService: LocationService,
        private readonly offerService: OfferService,
        private readonly settingsService: SettingsService,
        private readonly notificationService: NotificationService,
        private readonly interactionService: InteractionService
    ) {
    }

    // settings
    device_loadSettings(): Settings {
        return this.settingsService.loadSettings();
    }

    device_refreshRemoteSettings(): Promise<boolean> {
        return this.settingsService.refreshRemoteSettings();
    }

    device_saveSettings(settings: Settings): void {
        const lastSettings = this.device_loadSettings();
        if (lastSettings && settings.env !== lastSettings.env) {
            // TODO changing environment, so need to clear data and notifications
        }
        this.settingsService.saveSettings(settings, lastSettings);
    }

    device_info(): InitInfo {
        return this.userProfileService.deviceInfo;
    }

    resetData(): void {
        this.storageService.resetData();
    }

    interaction(type: InteractionType, comp: InteractionComponent, refType?: InteractionRefType, ref?: number) {
        this.interactionService.interaction(type, comp, refType, ref);
    }

    interactOffer(offerId: number, data: InteractionOffer) {
        this.interactionService.interactOffer(offerId, data);
    }

    interactAd(adId: number, data: InteractionAd) {
        this.interactionService.interactAd(adId, data);
    }

    interactOther(data: InteractionOther) {
        this.interactionService.interactOther(data);
    }

    interactTimerOffer(offerId: number, timerField: 'pgViewTime' | 'pgOpenTime' | 'pgShareTime' | 'pgShowCashierTime'): InteractionTimer {
        return this.interactionService.interactTimerOffer(offerId, timerField);
    }

    interactTimerAd(adId: number, timerField: 'pgViewTime' | 'pgVideoTime' | 'pgThanksTime' | 'pgSponsorTime'): InteractionTimer {
        return this.interactionService.interactTimerAd(adId, timerField);
    }

    interactTimerOther(timerField: 'pgWelcomeTime' | 'pgAboutTime' |
      'pgSettingsTime' | 'pgNoOffersTime' | 'pgLocationTime'): InteractionTimer {
        return this.interactionService.interactTimerOther(timerField);
    }

    device_clearData() {
        this.storageService.resetData();
    }

    // tickets
    tickets_getTicketCount(): number {
        return this.ticketService.ticketCount;
    }

    tickets_referralEligible(): boolean {
        return this.ticketService.referralEligible;
    }

    tickets_referralNotified(): boolean {
        return this.ticketService.referralNotified;
    }

    tickets_personalReferralCode(): string {
        return this.ticketService.personalReferralCode;
    }

    tickets_getAvailableTicketCount(): number {
        return this.ticketService.availableTicketCount;
    }

    tickets_setTickets(tickets: number) {
        return this.ticketService.setTickets(tickets);
    }

    tickets_notifyReferral() {
        return this.ticketService.notifyReferral();
    }

    tickets_fetchTickets(): Promise<void> {
        return this.ticketService.fetchTickets();
    }

    tickets_openedApp(): Promise<void> {
        return this.ticketService.openedApp();
    }

    tickets_openedOffer() {
        this.ticketService.openedOffer();
    }

    tickets_sharedOffer() {
        this.ticketService.sharedOffer();
    }

    tickets_filterOffers() {
        this.ticketService.filterOffers();
    }

    tickets_websiteVisit() {
        this.ticketService.websiteVisit();
    }

    tickets_redeemedLocalOffer(offerId: number, locationId: number, distance: number): Promise<void> {
        return this.ticketService.redeemedLocalOffer(offerId, locationId, distance);
    }

    tickets_redeemedOnlineOffer(offerId: number) {
        this.ticketService.redeemedOnlineOffer(offerId);
    }

    tickets_favoritedOffer() {
        this.ticketService.favoritedOffer();
    }

    tickets_resetDailyTasks() {
        this.ticketService.resetDailyTasks();
    }

    //
    // tickets_taskOpenApp() {
    // }
    //
    // tickets_taskRedeemLocalOffer() {
    // }
    //
    // tickets_taskRedeemOnlineOffer() {
    // }
    //
    // tickets_taskShare() {
    // }
    //
    // tickets_taskOpenOffer() {
    // }
    //
    // tickets_taskRevealOffer() {
    // }
    //
    // tickets_taskFavoriteOffer() {
    // }
    //
    // tickets_taskRedeemOnlineOffer() {
    // }

    // user
    user_initDevice(): Promise<InitInfo> {
        return this.userProfileService.initDevice();
    }

    user_isWelcomed(): boolean {
        return this.userProfileService.isWelcomed();
    }

    user_captureLocation(location: LocationInfoRPC): void {
        this.userProfileService.captureLocation(location);
    }

    user_setWelcomed(): void {
        this.userProfileService.setWelcomed();
    }

    user_isAuthenticated(): boolean {
        return this.userProfileService.isAuthenticated();
    }

    user_setProperty(property: UserProperty, value: string) {
        this.userProfileService.updateUserProperty(property, value);
    }

    user_register(firstName: string, lastName: string, email: string, password: string): Promise<UserLoginResult> {
        return this.userProfileService.register(firstName, lastName, email, password);
    }

    user_resendValidationCode(email: string): Promise<SuccessResult> {
        return this.userProfileService.resendValidationCode(email);
    }

    user_validate(validationCode: string): Promise<SuccessResult> {
        return this.clearSearchOnResult(this.userProfileService.validate(validationCode));
    }

    user_abortValidation(): void {
        return this.userProfileService.abortValidation();
    }

    user_login(email: string, password: string): Promise<UserLoginResult> {
        return this.clearSearchOnResult(this.userProfileService.login(email, password));
    }

    user_loginWithFacebook(): Promise<UserLoginResult> {
        return this.clearSearchOnResult(this.userProfileService.loginWithFacebook());
    }

    user_loginWithGoogle(): Promise<UserLoginResult> {
        return this.clearSearchOnResult(this.userProfileService.loginWithGoogle());
    }

    user_loginWithApple(): Promise<UserLoginResult> {
        return this.clearSearchOnResult(this.userProfileService.loginWithApple());
    }

    user_forgotPassword(email: string): Promise<SuccessResult> {
        return this.userProfileService.forgotPassword(email);
    }

    user_resetPassword(email: string, code: string, newPassword: string): Promise<UserLoginResult> {
        return this.clearSearchOnResult(this.userProfileService.resetPassword(email, code, newPassword));
    }

    user_needsValidation(): boolean {
        return this.userProfileService.needsValidation();
    }

    user_logout(): void {
        this.userProfileService.logout();
        this.offers_clearSearchResults();
    }

    user_info(): UserInfo {
        return this.userProfileService.userInfo;
    }

    user_alreadyRedeemed(): boolean {
        return this.userProfileService.alreadyRedeemed();
    }

    user_alreadyViewedShareInstructions(): boolean {
        return this.userProfileService.alreadyViewedShareInstructions();
    }

    user_setFirstRedemption(): UserInfo {
        return this.userProfileService.setFirstRedemption();
    }

    user_setFirstShare() {
        this.userProfileService.setFirstShare();
    }

    user_contactUs(reason: 'support' | 'merchants' | 'sponsors' | 'disable',
                   firstName: string, lastName: string, email: string, content: string): Promise<SuccessResult> {
        return this.userProfileService.contactUs(reason, firstName, lastName, email, content);
    }

    // location
    location_isAllowLocation(): Promise<boolean> {
        return this.locationService.isAllowLocation();
    }

    location_requestLocationPermission(): Promise<boolean> {
        return this.locationService.requestLocationPermission();
    }

    location_getLocationInfo(): Promise<LocationInfo> {
      try {
        console.log("location_getLocationInfo...");
        return this.locationService.getLocationInfo();
      } finally {
        console.log("location_getLoationInfo finished.");
      }
    }

    // offers
    offers_getSearch(): OfferSearch {
        return this.offerService.getSearch();
    }

    offers_getSearchResultLocation(): OfferSearchResultLocation {
        return this.storageService.loadOfferSearchResultLocation();
    }

    offers_clearSearchResults() {
        this.offers_setSearch(this.offers_getSearch());
    }

    offers_setSearch(search: OfferSearch) {
        this.offerService.setSearch(search);
    }

    offers_searchExpired(): boolean {
        return this.offerService.searchExpired();
    }

    offers_searchComplete(offerId: number = null): boolean {
        return this.offerService.searchComplete(offerId);
    }

    offers_haveOffers(): boolean {
        return this.offerService.haveOffers();
    }

    offers_loadOffers(includeRedeemed: boolean, filter: OfferFilter = {}, offerSet: 'free' | 'discount' |
      'all' | 'favorites' = 'free', location: LocationInfo = null): Array<OfferInfo> {
        return this.offerService.loadOffers(includeRedeemed, filter, offerSet);
    }

    offers_loadOfferImageURLs(offers: Array<OfferInfo>): Array<{ url: string; hash: string }> {
        return this.offerService.loadOfferImageURLs(offers);
    }

    offers_performSearch(): Promise<Array<OfferInfo>> {
        return this.offerService.performSearch();
    }

    offers_alternateLocations(search: OfferSearch = null, limit: number = 3): Promise<AlternateLocationsList> {
        return this.offerService.alternateLocations(search, limit);
    }

    offers_getOffer(offerId: number): OfferInfo {
        return this.offerService.getOffer(offerId);
    }

    async offers_getOfferViaApi(offerId: number): Promise<OfferInfo> {
      return await this.offerService.getOfferViaApi(offerId).then(value => {
        return value;
      });
    }

    offers_generateOfferShare(offerId: number): Promise<{ url: string; image: string }> {
        return this.offerService.generateOfferShare(offerId);
    }

    offer_nearbyOffer(offerId: number, addressId: number) {
        this.offerService.nearbyOffer(offerId, addressId);
    }

    offer_seenOffer(offerId: number) {
        this.offerService.seenOffer(offerId);
    }

    offer_watchedVideo(offerId: number): OfferInfo {
        return this.offerService.watchedVideo(offerId);
    }

    offers_revealOffer(offerId: number): Promise<OfferInfo> {
        return this.offerService.revealOffer(offerId);
    }

    offers_repeatUnlockOffer(offerId: number): Promise<OfferInfo> {
        return this.offerService.repeatUnlockOffer(offerId);
    }

    offers_favoriteOffer(offerId: number): Promise<OfferInfo> {
        return this.offerService.favoriteOffer(offerId);
    }

    offers_unfavoriteOffer(offerId: number): Promise<OfferInfo> {
        return this.offerService.unfavoriteOffer(offerId);
    }

    offers_redeemOffer(offerId: number, addressId: number): Promise<OfferInfo> {
        return this.offerService.redeemOffer(offerId, addressId);
    }

    offers_isAvailableOffer(offerId: number): Promise<OfferAvailableInfo> {
        return this.offerService.isAvailableOffer(offerId);
    }

    offers_testResetOffers(): Promise<SuccessResult> {
        return this.offerService.testResetOffers();
    }

    offers_recordOfferExperience(offerId: number, thumbs: 'up' | 'down' | 'skip'): SuccessResult {
        return this.offerService.recordOfferExperience(offerId, thumbs);
    }

    offers_recordOfferAdditionalPurchase(offerId: number, yesNo: 'yes' | 'no'): SuccessResult {
        return this.offerService.recordOfferAdditionalPurchase(offerId, yesNo);
    }

    offers_loadSponsor(): SponsorInfo {
        return this.offerService.loadSponsor();
    }

    offers_isShowSponsorThankYou(): boolean {
        const sponsor = this.offers_loadSponsor();
        return sponsor && sponsor.showThankYou;
    }

    offers_isShowSponsorPage(): boolean {
        const sponsor = this.offers_loadSponsor();
        return sponsor && sponsor.showSponsorPage;
    }

    offers_filterOptions(): OfferFilterOptions {
        return this.offerService.loadFilterOptions();
    }

    offers_totalSearchOffers(): number {
        const currentSearchResults: Array<number> = this.storageService.loadOffersListSearch();
        if (currentSearchResults && currentSearchResults.length > 0) {
            return currentSearchResults.length;
        }
        return 0;
    }

    // test actions
    test_resetWelcome() {
        this.userProfileService.testResetWelcomed();
    }

    test_resetFirstRedeem() {
        this.userProfileService.testResetFirstRedeem();
    }

    test_resetOffers(): Promise<SuccessResult> {
        return this.offers_testResetOffers();
    }

    test_resetFresh(): void {
        this.user_logout();
        this.storageService.resetLogin();
        this.storageService.resetData();
        this.test_resetWelcome();
        this.test_showRegister();
    }

    test_showRegister() {
        this.userProfileService.showRegister();
    }

    test_deleteUser(): Promise<SuccessResult> {
        this.offers_setSearch({location: true, radius: 50});
        return this.userProfileService.testDeleteUser();
    }

    test_resetOffer(offerId: number, resetRedeemed: boolean, resetRevealed: boolean, resetRepeatUnlocked: boolean,
                    resetThumbsup: boolean, resetBounceback: boolean, resetVideo: boolean): Promise<OfferInfo> {
        return this.offerService.testResetOffer(offerId, resetRedeemed, resetRevealed, resetRepeatUnlocked,
          resetThumbsup, resetBounceback, resetVideo);
    }

    private clearSearchOnResult<T>(p: Promise<T>): Promise<T> {
        return new Promise<T>((resolve, reject) => {
            p.then((result) => {
                this.offers_clearSearchResults();
                resolve(result);
            }, (err) => {
                reject(err);
            });
        });
    }
}
